import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { RouteTrackingService } from 'src/app/common/services/route-tracking.service';
import { StatusMessageComponent } from "src/app/common/components/core/status-message/status-message.component";
import { ButtonComponent, ButtonVariant } from 'src/app/common/components/core/button/button.component';
import { ConfirmationModalComponent } from 'src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { AccessDeniedConstants, AccessDeniedQueryParam, AccessDeniedReason } from './access-denied.constants';

@Component({
  selector: 'app-access-denied',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    StatusMessageComponent,
    ButtonComponent
],
  templateUrl: './access-denied.component.html'
})
export class AccessDeniedComponent implements OnInit {
  previousUrl!: string | string[];
  isSsoNoAccess = false;
  buttonVariant = ButtonVariant;
  constants = AccessDeniedConstants;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private routerTrackingService: RouteTrackingService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {
   }

  ngOnInit(): void {
    // Reached via fallbackRouteGuard (rehabconnect.com opened directly with an
    // SSO account that has no RC access) rather than an in-app feature guard —
    // there is no session and no meaningful "previous page" to go back to.
    this.isSsoNoAccess =
      this.route.snapshot.queryParamMap.get(AccessDeniedQueryParam.reason) === AccessDeniedReason.Sso;
    if (this.isSsoNoAccess) {
      return;
    }
    // 1. this route
    // 2. previous route for which there's no access
    // 3. route before the previous route
    this.previousUrl = this.routerTrackingService.getUrlFromHistory(3);
  }

  goBack(): void {
    this.routerTrackingService.removeLastUrl();
    this.routerTrackingService.removeLastUrl();
  }

  // Confirms before ending the myzPAX session: the user may still be using it
  // elsewhere (another tab), so logging out here isn't something to do silently.
  // Only on confirmation do we log the SSO session out entirely, so the next
  // login prompt lets the user pick a different account instead of silently
  // re-authenticating as the same account that has no Rehab Connect access.
  async tryDifferentAccount(): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            title: this.constants.logoutConfirmTitle,
            message: this.constants.logoutConfirmMessage,
            buttonText: this.constants.logoutConfirmButtonText,
            cancelText: this.constants.logoutConfirmCancelText,
            method: async () => {},
          },
          disableClose: true,
          autoFocus: false,
        })
        .afterClosed()
    );

    if (confirmed) {
      this.authService.redirectToSSOLogoutPage();
    }
  }

}
